import Axios from '../../../../axios'
import { Link } from 'react-router-dom'
import MUIDataTable from 'mui-datatables'
import { Breadcrumb } from '../../../components'
import React, { useState, useEffect, useCallback } from 'react'
import {
    Backdrop,
    CircularProgress,
    Grow,
    Icon,
    IconButton,
    TextField,
    Tooltip,
    Alert,
} from '@mui/material'
import { Box, styled } from '@mui/system'
import { API_URI } from '../../../../config'

const FlexBox = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
}))

const Container = styled('div')(({ theme }) => ({
    margin: '30px',
    [theme.breakpoints.down('sm')]: {
        margin: '16px',
    },
    '& .breadcrumb': {
        marginBottom: '30px',
        [theme.breakpoints.down('sm')]: {
            marginBottom: '16px',
        },
    },
}))

const ApprovedCustomer = () => {
    const [tableConfig, setTableConfig] = useState({
        page: 0,
        count: 0,
        rowsPerPage: 10,
    })
    const [userList, setUserList] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [searchValue, setSearchValue] = useState('')

    const fetchData = useCallback(async (page, rowsPerPage) => {
        try {
            setLoading(true)
            setError('')
            const { data } = await Axios.get(
                `${API_URI}/api/users/complete-await-passes/`,
                {
                    params: {
                        page,
                        rowsPerpage: rowsPerPage,
                    },
                }
            )
            setUserList(data?.user || [])
            setTableConfig((prev) => ({
                ...prev,
                page,
                rowsPerPage,
                count: data?.total || 0,
            }))
        } catch (err) {
            setError(
                err?.error ||
                    err?.message ||
                    'Failed to load members ready for pass.'
            )
            setUserList([])
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchSearchedValue = useCallback(async (value) => {
        if (!value?.trim()) return
        try {
            setLoading(true)
            setError('')
            const { data } = await Axios.get(
                `${API_URI}/api/user-find/get/complete-waiting/${encodeURIComponent(
                    value.trim()
                )}`
            )
            const results = Array.isArray(data) ? data : []
            setUserList(results)
            setTableConfig((prev) => ({
                ...prev,
                page: 0,
                count: results.length,
            }))
        } catch (err) {
            setError(err?.error || err?.message || 'Search failed.')
            setUserList([])
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (searchValue.trim()) {
            fetchSearchedValue(searchValue)
        } else {
            fetchData(tableConfig.page, tableConfig.rowsPerPage)
        }
    }, [tableConfig.page, tableConfig.rowsPerPage])

    const columns = [
        {
            name: 'memberId',
            label: 'Member Number',
            options: {
                filter: true,
            },
        },
        {
            name: 'firstName',
            label: 'First Name',
            options: {
                filter: true,
            },
        },
        {
            name: 'lastName',
            label: 'Last Name',
            options: {
                filter: true,
            },
        },
        {
            name: 'membershiptypeLongName',
            label: 'Membership Type',
            options: {
                filter: true,
            },
        },
        {
            name: 'email',
            label: 'Email',
            options: {
                filter: true,
            },
        },
        {
            name: 'action',
            label: ' ',
            options: {
                filter: false,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    return (
                        <FlexBox>
                            <Box flexGrow={1}></Box>
                            <Tooltip title="Edit Profile">
                                <Link to={`/pages/new-customer/${user?._id}`}>
                                    <IconButton>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </Link>
                            </Tooltip>
                            <Tooltip title="View Profile">
                                <Link to={`/pages/view-customer/${user?._id}`}>
                                    <IconButton>
                                        <Icon>arrow_right_alt</Icon>
                                    </IconButton>
                                </Link>
                            </Tooltip>
                        </FlexBox>
                    )
                },
            },
        },
    ]

    return (
        <Container>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <div className="breadcrumb">
                <Breadcrumb
                    routeSegments={[
                        { name: 'Pages', path: '/' },
                        { name: 'Members Ready To Receive Pass' },
                    ]}
                />
            </div>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            <Box overflow="auto">
                <Box minWidth={750}>
                    <MUIDataTable
                        title={'Members Ready Receive Pass'}
                        data={userList}
                        columns={columns}
                        options={{
                            filterType: 'checkbox',
                            draggableColumns: {
                                enabled: true,
                                transitionTime: 500,
                            },
                            responsive: 'vertical',
                            jumpToPage: true,
                            count: tableConfig.count,
                            selectableRows: 'none',
                            search: true,
                            filter: true,
                            print: false,
                            pagination: true,
                            viewColumns: true,
                            elevation: 1,
                            serverSide: true,
                            searchPlaceholder:
                                'Search by Member Number, Barcode or Email',
                            onSearchOpen: () => {
                                setSearchValue('')
                            },
                            onSearchClose: () => {
                                setSearchValue('')
                                fetchData(0, tableConfig.rowsPerPage)
                            },
                            rowsPerPageOptions: [10, 20, 40, 80, 100],
                            rowsPerPage: tableConfig.rowsPerPage,
                            page: tableConfig.page,
                            customSearchRender: (
                                searchText,
                                handleSearch,
                                hideSearch,
                                options
                            ) => {
                                return (
                                    <Grow appear in={true} timeout={300}>
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault()
                                                fetchSearchedValue(searchValue)
                                            }}
                                        >
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                value={searchValue}
                                                placeholder="Search by Member Number, Barcode or Email"
                                                onChange={({
                                                    target: { value },
                                                }) => setSearchValue(value)}
                                                InputProps={{
                                                    style: {
                                                        paddingRight: 0,
                                                    },
                                                    startAdornment: (
                                                        <Icon
                                                            fontSize="small"
                                                            sx={{ mr: 1 }}
                                                        >
                                                            search
                                                        </Icon>
                                                    ),
                                                    endAdornment: (
                                                        <IconButton
                                                            onClick={hideSearch}
                                                        >
                                                            <Icon fontSize="small">
                                                                clear
                                                            </Icon>
                                                        </IconButton>
                                                    ),
                                                }}
                                            />
                                        </form>
                                    </Grow>
                                )
                            },
                            onChangePage: (page) => {
                                setTableConfig((prev) => ({
                                    ...prev,
                                    page,
                                }))
                            },
                            onChangeRowsPerPage: (rowsPerPage) => {
                                setTableConfig((prev) => ({
                                    ...prev,
                                    rowsPerPage,
                                    page: 0,
                                }))
                            },
                            textLabels: {
                                body: {
                                    noMatch: loading
                                        ? 'Loading...'
                                        : 'No members ready to receive a pass.',
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        </Container>
    )
}

export default ApprovedCustomer
